import * as THREE from 'three';

export interface ParticleEmitOptions {
  count: number;
  position: THREE.Vector3;
  velocityRange: { min: THREE.Vector3; max: THREE.Vector3 };
  colorA: THREE.Color;
  colorB: THREE.Color;
  lifetime: number;
  gravity: number;
  sizeStart: number;
  sizeEnd: number;
  critical?: boolean;
}

// Per-particle state: [px, py, pz, vx, vy, vz, life, maxLife, r, g, b, sizeStart, gravity, sizeEnd]
const FIELDS = 14;

type SupplyDropCrater = {
  group: THREE.Group;
  ringMat: THREE.MeshBasicMaterial;
  scorchMat: THREE.MeshBasicMaterial;
  life: number;
  maxLife: number;
};

type BulletTracerStreak = {
  line: THREE.Line;
  mat: THREE.LineBasicMaterial;
  life: number;
  maxLife: number;
};

export class FpsParticleSystem {
  private mesh: THREE.InstancedMesh;
  private state: Float32Array;
  private maxParticles: number;
  private dummy = new THREE.Object3D();
  private tempColor = new THREE.Color();
  private frameMsAvg = 16.7;
  private activeParticles = 0;
  private optionalSkipFlip = false;
  private readonly supplyDropCraters: SupplyDropCrater[] = [];
  private readonly bulletTracers: BulletTracerStreak[] = [];

  constructor(private readonly scene: THREE.Scene, maxParticles: number) {
    this.maxParticles = maxParticles;
    this.state = new Float32Array(maxParticles * FIELDS);

    const geo = new THREE.BoxGeometry(0.08, 0.08, 0.08);
    const mat = new THREE.MeshStandardMaterial({ vertexColors: false, roughness: 0.8, metalness: 0.1 });
    this.mesh = new THREE.InstancedMesh(geo, mat, maxParticles);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.count = maxParticles;

    // Initialize all particles as dead (scale 0)
    for (let i = 0; i < maxParticles; i++) {
      this.dummy.scale.setScalar(0);
      this.dummy.position.set(0, -1000, 0);
      this.dummy.updateMatrix();
      this.mesh.setMatrixAt(i, this.dummy.matrix);
      this.mesh.setColorAt(i, new THREE.Color(0, 0, 0));
    }
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;

    this.scene.add(this.mesh);
  }

  /** Fast additive line streak for AR/SMG tracers at range. */
  spawnBulletTracer(
    origin: THREE.Vector3,
    end: THREE.Vector3,
    color: number,
    opacity = 0.82,
    fadeMs = 88,
  ): void {
    const len = origin.distanceTo(end);
    if (len < 0.25) return;

    const positions = new Float32Array([
      origin.x,
      origin.y,
      origin.z,
      end.x,
      end.y,
      end.z,
    ]);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const mat = new THREE.LineBasicMaterial({
      color,
      transparent: true,
      opacity,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const line = new THREE.Line(geo, mat);
    line.frustumCulled = false;
    line.renderOrder = 6;
    this.scene.add(line);

    this.bulletTracers.push({
      line,
      mat,
      life: fadeMs / 1000,
      maxLife: fadeMs / 1000,
    });
  }

  emit(opts: ParticleEmitOptions): void {
    const count = this.getThrottledCount(opts.count, opts.critical === true);
    if (count <= 0) return;

    let spawned = 0;
    for (let i = 0; i < this.maxParticles && spawned < count; i++) {
      const base = i * FIELDS;
      if (this.state[base + 6]! > 0) continue; // alive — skip

      const vMin = opts.velocityRange.min;
      const vMax = opts.velocityRange.max;

      this.state[base + 0] = opts.position.x;
      this.state[base + 1] = opts.position.y;
      this.state[base + 2] = opts.position.z;
      this.state[base + 3] = vMin.x + Math.random() * (vMax.x - vMin.x);
      this.state[base + 4] = vMin.y + Math.random() * (vMax.y - vMin.y);
      this.state[base + 5] = vMin.z + Math.random() * (vMax.z - vMin.z);
      this.state[base + 6] = opts.lifetime * (0.7 + Math.random() * 0.3); // life remaining
      this.state[base + 7] = opts.lifetime;   // maxLife
      this.state[base + 8] = opts.colorA.r;
      this.state[base + 9] = opts.colorA.g;
      this.state[base + 10] = opts.colorA.b;
      this.state[base + 11] = opts.sizeStart;
      this.state[base + 12] = opts.gravity;
      this.state[base + 13] = opts.sizeEnd;
      spawned++;
    }
  }

  /** Wispy contrail puffs behind supply flyover plane. */
  emitPlaneContrail(position: THREE.Vector3): void {
    this.emit({
      count: 2,
      position: position.clone(),
      velocityRange: {
        min: new THREE.Vector3(-0.35, -0.9, -0.35),
        max: new THREE.Vector3(0.35, -0.15, 0.35),
      },
      colorA: new THREE.Color(0.88, 0.94, 1.0),
      colorB: new THREE.Color(0.62, 0.72, 0.88),
      lifetime: 1.15,
      gravity: 0.04,
      sizeStart: 0.1,
      sizeEnd: 0.42,
      critical: true,
    });
  }

  /** Grey smoke puffs trailing a falling supply drop. */
  emitSupplyDropSmoke(position: THREE.Vector3): void {
    this.emit({
      count: 3,
      position: position.clone(),
      velocityRange: {
        min: new THREE.Vector3(-0.55, -1.1, -0.55),
        max: new THREE.Vector3(0.55, -0.25, 0.55),
      },
      colorA: new THREE.Color(0.42, 0.45, 0.5),
      colorB: new THREE.Color(0.24, 0.27, 0.32),
      lifetime: 0.95,
      gravity: 0.35,
      sizeStart: 0.2,
      sizeEnd: 0.62,
    });
  }

  /** Scorch ring decal where a supply crate lands. */
  spawnSupplyDropCrater(sceneRoot: THREE.Group, x: number, z: number): void {
    const group = new THREE.Group();
    group.position.set(x, 0.03, z);
    const ringMat = new THREE.MeshBasicMaterial({
      color: 0x2a2218,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const ring = new THREE.Mesh(new THREE.RingGeometry(0.55, 2.35, 30), ringMat);
    ring.rotation.x = -Math.PI / 2;
    group.add(ring);
    const scorchMat = ringMat.clone();
    const scorch = new THREE.Mesh(new THREE.CircleGeometry(0.64, 24), scorchMat);
    scorch.rotation.x = -Math.PI / 2;
    scorch.position.y = 0.004;
    group.add(scorch);
    sceneRoot.add(group);
    this.supplyDropCraters.push({
      group,
      ringMat,
      scorchMat,
      life: 0,
      maxLife: 14,
    });
  }

  update(dt: number): void {
    this.frameMsAvg += (dt * 1000 - this.frameMsAvg) * 0.08;
    let active = 0;

    for (let i = 0; i < this.maxParticles; i++) {
      const base = i * FIELDS;
      const life = this.state[base + 6]!;
      if (life <= 0) {
        // Hide dead particle
        this.dummy.position.set(0, -10000, 0);
        this.dummy.scale.setScalar(0);
        this.dummy.updateMatrix();
        this.mesh.setMatrixAt(i, this.dummy.matrix);
        continue;
      }
      active++;

      const maxLife = this.state[base + 7]!;
      const gravity = this.state[base + 12]!;
      const sizeEnd = this.state[base + 13]!;
      const sizeStart = this.state[base + 11]!;

      // Integrate
      this.state[base + 4]! -= gravity * dt; // apply gravity (vy decreases)
      this.state[base + 0]! += this.state[base + 3]! * dt;
      this.state[base + 1]! += this.state[base + 4]! * dt;
      this.state[base + 2]! += this.state[base + 5]! * dt;
      this.state[base + 6]! -= dt;

      const lifeRatio = Math.max(0, this.state[base + 6]! / maxLife); // 1 → 0
      const scale = sizeStart + (sizeEnd - sizeStart) * (1 - lifeRatio);

      this.dummy.position.set(this.state[base + 0]!, this.state[base + 1]!, this.state[base + 2]!);
      this.dummy.scale.setScalar(Math.max(0.001, scale));
      this.dummy.updateMatrix();
      this.mesh.setMatrixAt(i, this.dummy.matrix);

      // Interpolate color: colorA → black as life fades
      const r = this.state[base + 8]! * lifeRatio;
      const gv = this.state[base + 9]! * lifeRatio;
      const bv = this.state[base + 10]! * lifeRatio;
      this.tempColor.setRGB(r, gv, bv);
      this.mesh.setColorAt(i, this.tempColor);
    }

    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
    this.activeParticles = active;

    for (let i = this.bulletTracers.length - 1; i >= 0; i--) {
      const tracer = this.bulletTracers[i]!;
      tracer.life -= dt;
      const lifeRatio = Math.max(0, tracer.life / tracer.maxLife);
      tracer.mat.opacity = Math.max(0, lifeRatio);
      if (tracer.life <= 0) {
        tracer.line.removeFromParent();
        tracer.line.geometry.dispose();
        tracer.mat.dispose();
        this.bulletTracers.splice(i, 1);
      }
    }

    for (let i = this.supplyDropCraters.length - 1; i >= 0; i--) {
      const crater = this.supplyDropCraters[i]!;
      crater.life += dt;
      const fadeIn = Math.min(1, crater.life / 0.55);
      const fadeOut = crater.life > crater.maxLife - 2.2
        ? Math.max(0, 1 - (crater.life - (crater.maxLife - 2.2)) / 2.2)
        : 1;
      const opacity = 0.58 * fadeIn * fadeOut;
      crater.ringMat.opacity = opacity;
      crater.scorchMat.opacity = opacity * 0.82;
      crater.group.scale.setScalar(0.92 + fadeIn * 0.08);
      if (crater.life >= crater.maxLife) {
        crater.group.removeFromParent();
        crater.ringMat.dispose();
        crater.scorchMat.dispose();
        this.supplyDropCraters.splice(i, 1);
      }
    }
  }

  getActiveParticleCount(): number {
    return this.activeParticles;
  }

  getMaxParticles(): number {
    return this.maxParticles;
  }

  /** Upward water droplets on swim entry or fast surface movement. */
  emitWaterSplash(position: THREE.Vector3, intensity = 1): void {
    const scale = Math.max(0.35, Math.min(1.4, intensity));
    this.emit({
      count: Math.round(14 * scale),
      position,
      velocityRange: {
        min: new THREE.Vector3(-2.4 * scale, 1.2, -2.4 * scale),
        max: new THREE.Vector3(2.4 * scale, 5.2 * scale, 2.4 * scale),
      },
      colorA: new THREE.Color(0.52, 0.8, 0.98),
      colorB: new THREE.Color(0.24, 0.54, 0.86),
      lifetime: 0.36,
      gravity: 5.8,
      sizeStart: 0.065 * scale,
      sizeEnd: 0.008,
      critical: true,
    });
  }

  private getThrottledCount(count: number, critical: boolean): number {
    if (this.frameMsAvg < 26) return count;
    if (critical) return Math.max(1, Math.ceil(count * (this.frameMsAvg > 38 ? 0.6 : 0.8)));
    if (this.frameMsAvg > 42) {
      this.optionalSkipFlip = !this.optionalSkipFlip;
      if (this.optionalSkipFlip) return 0;
      return Math.max(1, Math.ceil(count * 0.25));
    }
    if (this.frameMsAvg > 34) return Math.max(1, Math.ceil(count * 0.4));
    return Math.max(1, Math.ceil(count * 0.65));
  }

  dispose(): void {
    for (const tracer of this.bulletTracers) {
      tracer.line.removeFromParent();
      tracer.line.geometry.dispose();
      tracer.mat.dispose();
    }
    this.bulletTracers.length = 0;
    for (const crater of this.supplyDropCraters) {
      crater.group.removeFromParent();
      crater.ringMat.dispose();
      crater.scorchMat.dispose();
    }
    this.supplyDropCraters.length = 0;
    this.mesh.parent?.remove(this.mesh);
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}
